import { RefreshCw, ArrowUpDown, Search, X } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { TaskCard } from './TaskCard';
import { useTaskSort, SORT_OPTIONS, type TaskSortField } from './hooks/useTaskSort';
import type { Task } from '@/api/tasks';

interface TaskListProps {
  tasks: Task[];
  isLoading?: boolean;
  isRefetching?: boolean;
  onComplete: (id: string) => void;
  onSelect?: (task: Task) => void;
  completingId?: string;
}

function TaskListSkeleton() {
  return (
    <div className="space-y-2 p-4">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="p-3 rounded-lg border flex gap-3">
          <Skeleton className="h-8 w-8 rounded-lg" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function TaskList({
  tasks,
  isLoading,
  isRefetching,
  onComplete,
  onSelect,
  completingId,
}: TaskListProps) {
  // Guard against non-array response
  const taskArray = Array.isArray(tasks) ? tasks : [];
  const pendingTasks = taskArray.filter((t) => t.status === 'PENDING');
  const completedTasks = taskArray.filter((t) => t.status === 'COMPLETED');

  // Sorting and search (only for pending tasks)
  const { sortField, setSort, searchQuery, setSearch, sortedTasks: sortedPending } = useTaskSort(pendingTasks);

  if (isLoading) {
    return <TaskListSkeleton />;
  }

  if (pendingTasks.length === 0 && completedTasks.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-muted-foreground">Нет задач</p>
      </div>
    );
  }

  const currentSortLabel = SORT_OPTIONS.find((o) => o.value === sortField)?.label || 'По сроку';

  return (
    <div className="relative">
      {/* Refetching indicator */}
      {isRefetching && (
        <div className="absolute top-2 right-4 z-10">
          <RefreshCw className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Search input */}
      <div className="px-4 pt-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Поиск задач..."
            value={searchQuery}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 pr-9 h-9"
          />
          {searchQuery && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
              onClick={() => setSearch('')}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Sort control and count */}
      <div className="px-4 pt-2 pb-1 flex items-center justify-between">
        <span className="text-xs text-muted-foreground">
          {sortedPending.length === pendingTasks.length
            ? `${sortedPending.length} ${sortedPending.length === 1 ? 'задача' : sortedPending.length < 5 ? 'задачи' : 'задач'}`
            : `${sortedPending.length} из ${pendingTasks.length}`}
        </span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-7 text-xs gap-1">
              <ArrowUpDown className="h-3 w-3" />
              {currentSortLabel}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuRadioGroup
              value={sortField}
              onValueChange={(v) => setSort(v as TaskSortField)}
            >
              {SORT_OPTIONS.map((option) => (
                <DropdownMenuRadioItem key={option.value} value={option.value}>
                  {option.label}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Pending tasks */}
      <div className="px-4 pb-4 space-y-2">
        {sortedPending.map((task) => (
          <TaskCard
            key={task.id}
            task={task}
            onComplete={onComplete}
            onSelect={onSelect}
            isCompleting={completingId === task.id}
          />
        ))}
      </div>

      {/* Completed tasks section */}
      {completedTasks.length > 0 && (
        <div className="border-t">
          <div className="px-4 py-2 bg-muted/50">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Выполненные ({completedTasks.length})
            </span>
          </div>
          <div className="p-4 space-y-2">
            {completedTasks.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                onComplete={onComplete}
                onSelect={onSelect}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
